"use client";

import { useLocale, useTranslations } from "next-intl";
import { usePathname, useRouter } from "next/navigation";
import { ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";

import Icon from "@/components/ui/Icon/Icon";
import Modal from "@/components/ui/Modal/Modal";
import ModalContent from "@/components/ui/Modal/ModalContent";
import { UTMParametersContext, getFallbackCampaignParameters } from "@/context/UTMParametersContext";
import { UserContext } from "@/context/UserContext";
import useIsSaleScreen from "@/hooks/useIsSaleScreen";
import useMediaQuery from "@/hooks/useMediaQuery";

import CreateScheduleCallForm from "../CreateScheduleCallModal/CreateScheduleCallForm";
import { InquiryDataType } from "../StartChat/StartChat";
// import { InquiryDataType } from "../StartChat";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "../ui/Button/Button";
import { CheckoutState } from "./CheckoutProvider";

interface AssistanceModalData {
  entry: AssistanceModalEntry;
  isInventoryAvaialable: string;
  locale: string;
  isCheckoutPage?: boolean;
}

interface AssistanceModalEntry {
  id: string;
  type: "assistanceModal";
  internalName: string;
  title: string;
  subTitle: string;
  ctas: CustomLink[];
  intentModalShowAfter: number;
  isIntentLeadForm: boolean;
  leadFormSubtitle?: string;
  enableRecurrence?: boolean;
}

interface CustomLink {
  id: string;
  type: "customLink";
  label: string;
  link: LinkDetails;
  validator: string;
  isVideo?: boolean;
}

interface LinkDetails {
  linkType: "external";
  visualType: "label" | "icon_label";
  url: string;
  target: "_self" | "_blank";
  icon?: string;
}

interface Props {
  formAction: (data: InquiryDataType) => void;
  loading: (v: boolean) => void;
}

export default function CreateIntentLeadForm(Props: AssistanceModalData) {
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const pathname = usePathname();
  const isSo = useIsSaleScreen();
  const [openIntentModal, setOpenIntentModal] = useState(false);
  const [pageTitle, setPageTitle] = useState("");
  const idleTimer = useRef<NodeJS.Timeout | null>(null);
  const hasInitialized = useRef(false);
  const { setIsDialogueOpen, setCheckoutLeadData, setIsCheckoutIntentModal, isCheckoutBackClicked, setIsCheckoutBackClicked } =
    useContext(UserContext);
  const { utmParameters } = useContext(UTMParametersContext);
  const whatsappNumber = t("label.whatsapp.number") || "97144016335";
  const checkoutData = JSON.parse(localStorage.getItem("checkoutData") || "{}") as CheckoutState;

  // Set isCheckoutIntentModal to true when component mounts on checkout page with intent form
  useEffect(() => {
    if (Props.isCheckoutPage && Props.entry.isIntentLeadForm) {
      setIsCheckoutIntentModal(true);
    }
    return () => {
      if (Props.isCheckoutPage) {
        setIsCheckoutIntentModal(false);
      }
    };
  }, [Props.isCheckoutPage, Props.entry.isIntentLeadForm]);

  // Generate a unique key for localStorage based on the modal entry
  const modalStorageKey = `intentModal_${Props.entry.id}_shown_and_closed`;

  // Check if modal has been shown and closed (and should not reappear if enableRecurrence is false)
  const getHasBeenShownAndClosed = () => {
    if (typeof window !== "undefined") {
      return localStorage.getItem(modalStorageKey) === "true";
    }
    return false;
  };

  // Mark modal as shown and closed
  const setHasBeenShownAndClosed = () => {
    if (typeof window !== "undefined" && !Props.entry.enableRecurrence) {
      localStorage.setItem(modalStorageKey, "true");
    }
  };

  // Reset the "shown and closed" flag (used when back button is clicked)
  const resetHasBeenShownAndClosed = () => {
    if (typeof window !== "undefined") {
      localStorage.removeItem(modalStorageKey);
    }
  };

  // Takes the name of the property from the metadata and sets the label that is sent with the WhatsApp message
  useEffect(() => {
    if (typeof window !== "undefined") {
      if (window?.location?.pathname.includes("projects")) {
        const title = document?.title;
        setPageTitle(title);
      }
    }
  }, []);

  useEffect(() => {
    if (isCheckoutBackClicked && Props.entry.isIntentLeadForm) {
      resetHasBeenShownAndClosed();
      setOpenIntentModal(true);
    }
  }, [isCheckoutBackClicked]);

  const parameters: { [key: string]: string } = {
    ...getFallbackCampaignParameters(utmParameters?.utm_medium),
    ...utmParameters,
  };

  const getLocalStorageParams = () => {
    if (typeof window !== "undefined") {
      const params = localStorage.getItem("utmParameters");
      if (params) {
        const parsedParams = JSON.parse(params);
        const searchParams = new URLSearchParams(parsedParams);
        return searchParams.toString();
      }
    }
    return "";
  };

  const whatsappURL = useMemo(() => {
    if (typeof window !== "undefined") {
      // Get base URL without query parameters
      const currentUrl = new URL(window.location.href);
      const baseUrl = `${currentUrl.origin}${currentUrl.pathname}`;

      // Parse existing URL parameters
      const currentParams = new URLSearchParams(currentUrl.search);

      // Get localStorage parameters
      const localStorageParams = getLocalStorageParams();

      // Properly type the object with index signature
      const localStorageParamsObj: Record<string, string> = {};

      if (localStorageParams) {
        // Convert localStorageParams string to object
        const searchParams = new URLSearchParams(localStorageParams);
        searchParams.forEach((value, key) => {
          localStorageParamsObj[key] = value;
        });
      }

      // Create a new URLSearchParams instance for the final parameters
      const finalParams = new URLSearchParams();

      // Priority: localStorage parameters over URL parameters
      // Add current URL parameters first (lower priority)
      currentParams.forEach((value, key) => {
        finalParams.append(key, value);
      });

      // Then override with localStorage parameters (higher priority)
      Object.entries(localStorageParamsObj).forEach(([key, value]) => {
        finalParams.set(key, String(value)); // Explicitly convert to string
      });

      // Construct the final landing page URL - CHECK IF PARAMS EXIST TO AVOID ?&
      const paramsString = finalParams.toString();
      const finalLandingPageURL = paramsString ? `${baseUrl}?${paramsString}` : baseUrl;

      // Create search params from campaign parameters
      const searchParams = new URLSearchParams(
        parameters["campaign_id"] === "a120Y000000uLMj" ? { campaign_id: parameters["campaign_id"] } : parameters,
      );
      searchParams.delete("fullLandingPageUrl");
      searchParams.delete("landingPageURL");

      // Check if the URL already contains utm_medium or campaign_id
      // Parse the finalLandingPageURL to check for duplicate parameters
      const finalUrlObj = new URL(finalLandingPageURL);
      const finalUrlParams = finalUrlObj.searchParams;

      // When constructing the final WhatsApp URL text, check if parameters already exist
      const textUrlParams = new URLSearchParams(searchParams.toString());

      // Specifically check and remove duplicates for utm_medium and campaign_id
      if (finalUrlParams.has("utm_medium") && textUrlParams.has("utm_medium")) {
        // Keep the one from finalUrlParams (from localStorage/URL) and remove from textUrlParams
        textUrlParams.delete("utm_medium");
      }

      if (finalUrlParams.has("campaign_id") && textUrlParams.has("campaign_id")) {
        // Keep the one from finalUrlParams (from localStorage/URL) and remove from textUrlParams
        textUrlParams.delete("campaign_id");
      }

      return {
        finalLandingPageURL,
        searchParams: textUrlParams,
      };
    }
    return undefined;
  }, [parameters]);

  // useEffect(() => {
  //   const resetTimer = () => {
  //     if (idleTimer.current) clearTimeout(idleTimer.current);
  //     if (!openIntentModal) {
  //       idleTimer.current = setTimeout(() => setOpenIntentModal(true), Number(Props.entry.intentModalShowAfter) * 1000);
  //     }
  //   };

  //   window.addEventListener("mousemove", resetTimer);
  //   window.addEventListener("keydown", resetTimer);
  //   window.addEventListener("touchstart", resetTimer);

  //   resetTimer();

  //   return () => {
  //     if (idleTimer.current) clearTimeout(idleTimer.current);
  //     window.removeEventListener("mousemove", resetTimer);
  //     window.removeEventListener("keydown", resetTimer);
  //     window.removeEventListener("touchstart", resetTimer);
  //   };
  // }, []);

  useEffect(() => {
    // Prevent modal from opening on mount if it was already shown and closed
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      if (getHasBeenShownAndClosed() && !Props.entry.enableRecurrence) {
        return;
      }
    }

    const resetTimer = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);

      // If modal is open, don't start a new timer
      if (openIntentModal) return;

      // If it was already shown and closed, and recurrence is OFF, don't start a new timer
      if (getHasBeenShownAndClosed() && !Props.entry.enableRecurrence) return;

      idleTimer.current = setTimeout(
        () => {
          setOpenIntentModal(true);
        },
        Number(Props.entry.intentModalShowAfter) * 1000,
      );
    };

    window.addEventListener("mousemove", resetTimer);
    window.addEventListener("keydown", resetTimer);
    window.addEventListener("touchstart", resetTimer);

    resetTimer();

    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      window.removeEventListener("mousemove", resetTimer);
      window.removeEventListener("keydown", resetTimer);
      window.removeEventListener("touchstart", resetTimer);
    };
  }, [openIntentModal, Props.entry.enableRecurrence, Props.entry.intentModalShowAfter]);

  function item(value: CustomLink, index: number, array: CustomLink[]): ReactNode {
    throw new Error("Function not implemented.");
  }

  if (isSo) {
    return null;
  }

  return (
    <Modal
      open={openIntentModal}
      onClose={() => {
        setOpenIntentModal(false);
        setHasBeenShownAndClosed();
        if (isCheckoutBackClicked) {
          setIsCheckoutBackClicked?.(false);
          setIsCheckoutIntentModal(false);
          router.back();
        }
      }}
    >
      <ModalContent className="w-full md:w-12\/24 p-l max-h-[90dvh]">
        <div className="flex justify-between mb-l">
          <h2 className="font-primary font-medium text-m uppercase">{Props.entry.title}</h2>
          <Button
            className="modal__close"
            size={ButtonSize.MEDIUM}
            variant={ButtonVariant.TERTIARY}
            theme={ButtonTheme.BLACK}
            onClick={() => {
              setOpenIntentModal(false);
              setHasBeenShownAndClosed();
              if (isCheckoutBackClicked) {
                setIsCheckoutBackClicked?.(false);
                setIsCheckoutIntentModal(false);
                router.back();
              }
            }}
          >
            <span className="pointer-events-none">
              <Icon name="close" size="24" title="close" className="stroke-black" />
              <span className="sr-only">{t("button.label.closeModal")}</span>
            </span>
          </Button>
        </div>
        {Props.isCheckoutPage && !checkoutData.inquiryId ? (
          <>
            <p className="mb-l font-secondary">{Props.entry.leadFormSubtitle}</p>
            <CreateScheduleCallForm
              mode={"scheduleCall"}
              closeModal={() => {
                setOpenIntentModal(false);
                setHasBeenShownAndClosed();
              }}
              leadFormPageLocation={"leadFormPageLocation"}
              isIntentForm={true}
            />
          </>
        ) : Props.entry.isIntentLeadForm && !Props.isCheckoutPage ? (
          <>
            <p className="mb-l font-secondary">{Props.entry.leadFormSubtitle}</p>
            <CreateScheduleCallForm
              mode={"scheduleCall"}
              closeModal={() => {
                setOpenIntentModal(false);
                setHasBeenShownAndClosed();
              }}
              leadFormPageLocation={"leadFormPageLocation"}
              isIntentForm={true}
            />
          </>
        ) : null}
        <p className="mb-m mt-m font-secondary">{Props.entry.subTitle}</p>
        <div className="flex flex-wrap gap-[10px] md:overflow-visible">
          {Props?.entry?.ctas &&
            Props?.entry?.ctas.map((item) => (
              <Button
                key={item.id}
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.MEDIUM}
                theme={ButtonTheme.BLACK}
                isIntentForm={true}
                href={
                  item?.link?.url?.includes("whatsapp")
                    ? !!whatsappURL
                      ? `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${
                          pathname?.includes("projects")
                            ? encodeURIComponent(
                                t("text.project.pages", {
                                  projectName: pageTitle,
                                  url: `${whatsappURL?.finalLandingPageURL}${whatsappURL?.finalLandingPageURL?.includes("?") ? "&" : "?"}${whatsappURL?.searchParams}`,
                                }),
                              )
                            : encodeURIComponent(
                                t("text.generic.pages", {
                                  url: `${whatsappURL?.finalLandingPageURL}${whatsappURL?.finalLandingPageURL?.includes("?") ? "&" : "?"}${whatsappURL?.searchParams}`,
                                }),
                              )
                        }`
                      : t("text.generic.pages", {
                          url: `https://api.whatsapp.com/send?phone=${whatsappNumber}&campaign_id=${parameters["campaign_id"]}`,
                        })
                    : item.link.url
                }
                customClass="flex justify-center items-center whitespace-nowrap"
                onClick={(e) => {
                  if ((item?.link?.url ?? "").indexOf("chat") >= 0) {
                    e.preventDefault();
                    setHasBeenShownAndClosed();
                    if (checkoutData?.inquiryId && checkoutData?.inquiryNumber && Props?.isCheckoutPage) {
                      const inquiryData: InquiryDataType = {
                        inquiryID: checkoutData.inquiryId || "",
                        inquiryName: checkoutData.inquiryNumber || "",
                        campaignName: "DAMACProperties.Dig-Dir/Org-Dir-Jan22",
                        customerName: checkoutData.firstName + " " + checkoutData.lastName,
                        tSAEmail: "sftibcoexceptions@damacgroup.com",
                      };
                      setCheckoutLeadData(inquiryData);
                      setIsCheckoutIntentModal(true);
                      setOpenIntentModal(false);
                    } else {
                      setIsDialogueOpen(true);
                      setOpenIntentModal(false);
                      setIsCheckoutBackClicked?.(false);
                    }
                  }
                }}
              >
                {
                  <span className="flex items-center mr-s rtl:mr-0 rtl:ml-xs">
                    <Icon className="flex" name={item.link.icon || ""} size="16" title="" />
                  </span>
                }
                <span>{item.label}</span>
              </Button>
            ))}
        </div>
      </ModalContent>
    </Modal>
  );
}
